import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LookProvider, PinDark, useLook } from "@/context/LookContext";
import MyLook from "@/pages/MyLook";
import Chores from "@/pages/Chores";
import PiggyBankPage from "@/pages/PiggyBank";
import WishList from "@/pages/WishList";
import SavingsGoals from "@/pages/SavingsGoals";
import { Loader } from "@/components/common";
import AppShell, { AdminShell, SectionGuard } from "@/components/AppShell";
import { ForgotPassword, ResetPassword } from "@/pages/AccountPages";
import { AdminAdmins, AdminAudit, AdminFamilies, AdminHome, AdminUsers } from "@/pages/Admin";
import Landing from "@/pages/Landing";
import SignIn from "@/pages/SignIn";
import SignUp from "@/pages/SignUp";
import KidSignIn from "@/pages/KidSignIn";
import Onboarding from "@/pages/Onboarding";
import InvitePage from "@/pages/InvitePage";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import Bills from "@/pages/Bills";
import Spending from "@/pages/Spending";
import Members from "@/pages/Members";
import Settings from "@/pages/Settings";
import ComingSoon from "@/pages/ComingSoon";
import Lists from "@/pages/Lists";
import CalendarPage from "@/pages/Calendar";
import Flights from "@/pages/Flights";
import CalendarLinks from "@/pages/CalendarLinks";
import Shortcuts from "@/pages/Shortcuts";
import Messages from "@/pages/Messages";
import VideoCalls from "@/pages/VideoCalls";
import Unsubscribe from "@/pages/Unsubscribe";
import Billing from "@/pages/Billing";
import GiftPage from "@/pages/GiftPage";
import Meals from "@/pages/Meals";
import StoryTime from "@/pages/StoryTime";
import Bedtime from "@/pages/Bedtime";
import ScreenTime from "@/pages/ScreenTime";
import LearnPlay from "@/pages/LearnPlay";
import HomeCare from "@/pages/HomeCare";
import Budgets from "@/pages/Budgets";
import Splits from "@/pages/Splits";
import WhosWhere from "@/pages/WhosWhere";
import Memories from "@/pages/Memories";
import YearReview from "@/pages/YearReview";
import Documents from "@/pages/Documents";
import Inventory from "@/pages/Inventory";
import Insurance from "@/pages/Insurance";
import Vehicles from "@/pages/Vehicles";
import Pets from "@/pages/Pets";
import Health from "@/pages/Health";
import School from "@/pages/School";
import Birthdays from "@/pages/Birthdays";
import Coparent from "@/pages/Coparent";
import Subscriptions from "@/pages/Subscriptions";
import Scanning from "@/pages/Scanning";
import Printables from "@/pages/Printables";
import EtaPublic from "@/pages/EtaPublic";
import Lease from "@/pages/Lease";
import Shutoffs from "@/pages/Shutoffs";
import EmergencyPrep from "@/pages/EmergencyPrep";
import HouseholdManual from "@/pages/HouseholdManual";
import AwayChecklist from "@/pages/AwayChecklist";
import Roommates from "@/pages/Roommates";
import TaxFolder from "@/pages/TaxFolder";
import { Privacy, Terms } from "@/pages/Legal";
import Projects from "@/pages/Projects";
import Library from "@/pages/Library";
import AdminExplainers from "@/pages/AdminExplainers";
import AdminPartnerBrief from "@/pages/AdminPartnerBrief";
import AdminPatentPacket from "@/pages/AdminPatentPacket";
import AdminCompetitorBrief from "@/pages/AdminCompetitorBrief";
import AdminSidebarGuide from "@/pages/AdminSidebarGuide";
import AdminPlans from "@/pages/AdminPlans";
import Irregular from "@/pages/Irregular";
import IfSomethingHappens from "@/pages/IfSomethingHappens";
import CareProfile, { CareInviteAccept } from "@/pages/CareProfile";
import MoveLog from "@/pages/MoveLog";
import Maintenance from "@/pages/Maintenance";
import HousingCosts from "@/pages/HousingCosts";
import Utilities from "@/pages/Utilities";
import CoparentJoin from "@/pages/CoparentJoin";
import VaultReset from "@/pages/VaultReset";
import Emergency from "@/pages/Emergency";
import SitterView from "@/pages/SitterView";
import ListDetail from "@/pages/ListDetail";
import NotificationSettings from "@/pages/NotificationSettings";
import AccountSettings, { AccountDeleted } from "@/pages/AccountSettings";
import ExportDownload from "@/pages/ExportDownload";
import Copilot, { KidsChats } from "@/pages/Copilot";
import CopilotMemory from "@/pages/CopilotMemory";
import ConnectedAI from "@/pages/ConnectedAI";
import ConnectAuthorize from "@/pages/ConnectAuthorize";

function RedirectToHub() {
  const { session } = useAuth();
  const { pathname } = useLocation();
  if (session === null) return <Loader />;
  if (!session) return <Navigate to="/signin" replace />;
  if (!session.last_organization_id) return <Navigate to={session.user?.platform_role ? "/admin/home" : "/onboarding"} replace />;
  return <Navigate to={`/h/${session.last_organization_id}${pathname}`} replace />;
}

function NotFound() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-q-bg px-6 text-center" data-testid="not-found-page">
      <p className="font-display text-6xl font-extrabold text-q-wordmark">404</p>
      <p className="mt-3 text-q-muted">This page doesn't exist.</p>
      <a href="/signin" className="q-btn q-btn-primary mt-6" data-testid="not-found-signin-link">Go to sign in</a>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Process a Google session_id before anything else checks the session.
  if (location.hash?.includes("session_id=") && !sessionStorage.getItem("qhh.reauth")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<PinDark><Landing /></PinDark>} />
      <Route path="/signin" element={<PinDark><SignIn /></PinDark>} />
      <Route path="/privacy" element={<PinDark><Privacy /></PinDark>} />
      <Route path="/terms" element={<PinDark><Terms /></PinDark>} />
      <Route path="/signup" element={<PinDark><SignUp /></PinDark>} />
      <Route path="/kid" element={<PinDark><KidSignIn /></PinDark>} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/vault-reset" element={<VaultReset />} />
      <Route path="/sitter/:token" element={<SitterView />} />
      <Route path="/eta/:token" element={<EtaPublic />} />
      <Route path="/connect/authorize" element={<PinDark><ConnectAuthorize /></PinDark>} />
      <Route path="/invite/:token" element={<PinDark><InvitePage /></PinDark>} />
      <Route path="/coparent/join/:token" element={<PinDark><CoparentJoin /></PinDark>} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/forgot-password" element={<PinDark><ForgotPassword /></PinDark>} />
      <Route path="/account-deleted" element={<AccountDeleted />} />
      <Route path="/download/:id" element={<ExportDownload />} />
      <Route path="/reset-password/:token" element={<PinDark><ResetPassword /></PinDark>} />
      <Route path="/preview/*" element={<NotFound />} />
      <Route path="/admin" element={<AdminShell />}>
        <Route index element={<Navigate to="home" replace />} />
        <Route path="home" element={<AdminHome />} />
        <Route path="families" element={<AdminFamilies />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="admins" element={<AdminAdmins />} />
        <Route path="audit-log" element={<AdminAudit />} />
        <Route path="explainers" element={<AdminExplainers />} />
        <Route path="partner-brief" element={<AdminPartnerBrief />} />
        <Route path="patent-packet" element={<AdminPatentPacket />} />
        <Route path="briefs/competitor" element={<AdminCompetitorBrief />} />
        <Route path="briefs/combined" element={<AdminCompetitorBrief slug="combined" />} />
        <Route path="briefs/sidebar-guide" element={<AdminSidebarGuide />} />
        <Route path="plans" element={<AdminPlans />} />
        <Route path="my-look" element={<MyLook />} />
        <Route path="*" element={<Navigate to="home" replace />} />
      </Route>
      <Route path="/h/:hubId" element={<AppShell />}>
        <Route index element={<Navigate to="home" replace />} />
        <Route path="home" element={<Home />} />
        <Route path="money/bills" element={<SectionGuard section="money"><Bills /></SectionGuard>} />
        <Route path="money/spending" element={<SectionGuard section="money"><Spending /></SectionGuard>} />
        <Route path="money/savings-goals" element={<SectionGuard section="money"><SavingsGoals /></SectionGuard>} />
        <Route path="money/budgets" element={<SectionGuard section="money"><Budgets /></SectionGuard>} />
        <Route path="money/splits" element={<SectionGuard section="splits"><Splits /></SectionGuard>} />
        <Route path="kids/chores" element={<SectionGuard section="kids"><Chores /></SectionGuard>} />
        <Route path="kids/piggy-bank" element={<SectionGuard section="kids"><PiggyBankPage /></SectionGuard>} />
        <Route path="kids/wish-list" element={<SectionGuard section="kids"><WishList /></SectionGuard>} />
        <Route path="family/members" element={<SectionGuard section="family"><Members /></SectionGuard>} />
        <Route path="family/settings" element={<SectionGuard section="family"><Settings /></SectionGuard>} />
        <Route path="family/my-look" element={<MyLook />} />
        <Route path="lists" element={<SectionGuard section="lists"><Lists /></SectionGuard>} />
        <Route path="kids/story-time" element={<SectionGuard section="kids"><StoryTime /></SectionGuard>} />
        <Route path="kids/learn" element={<SectionGuard section="kids"><LearnPlay /></SectionGuard>} />
        <Route path="kids/screen-time" element={<SectionGuard section="kids"><ScreenTime /></SectionGuard>} />
        <Route path="kids/bedtime" element={<SectionGuard section="kids"><Bedtime /></SectionGuard>} />
        <Route path="meals" element={<SectionGuard section="meals"><Meals /></SectionGuard>} />
        <Route path="school" element={<SectionGuard section="school"><School /></SectionGuard>} />
        <Route path="birthdays" element={<SectionGuard section="birthdays"><Birthdays /></SectionGuard>} />
        <Route path="coparent" element={<SectionGuard section="coparent"><Coparent /></SectionGuard>} />
        <Route path="subscriptions" element={<SectionGuard section="subscriptions"><Subscriptions /></SectionGuard>} />
        <Route path="printables" element={<Printables />} />
        <Route path="scanning" element={<SectionGuard section="scanning"><Scanning /></SectionGuard>} />
        <Route path="family/health" element={<SectionGuard section="health"><Health /></SectionGuard>} />
        <Route path="family/emergency" element={<SectionGuard section="emergency"><Emergency /></SectionGuard>} />
        <Route path="home/inventory" element={<SectionGuard section="inventory"><Inventory /></SectionGuard>} />
        <Route path="home/insurance" element={<SectionGuard section="insurance"><Insurance /></SectionGuard>} />
        <Route path="shutoffs" element={<SectionGuard section="shutoffs"><Shutoffs /></SectionGuard>} />
        <Route path="shutoffs/:cardId" element={<SectionGuard section="shutoffs"><Shutoffs /></SectionGuard>} />
        <Route path="prep" element={<SectionGuard section="prep"><EmergencyPrep /></SectionGuard>} />
        <Route path="roommates" element={<Roommates />} />
        <Route path="projects" element={<SectionGuard section="home"><Projects /></SectionGuard>} />
        <Route path="projects/:pid" element={<SectionGuard section="home"><Projects /></SectionGuard>} />
        <Route path="library" element={<Library />} />
        <Route path="library/:eid" element={<Library />} />
        <Route path="money/tax" element={<SectionGuard section="money"><TaxFolder /></SectionGuard>} />
        <Route path="money/irregular" element={<SectionGuard section="money"><Irregular /></SectionGuard>} />
        <Route path="family/if-something-happens" element={<IfSomethingHappens />} />
        <Route path="care/:pid" element={<SectionGuard section="health"><CareProfile /></SectionGuard>} />
        <Route path="care-invite/:token" element={<SectionGuard section="health"><CareInviteAccept /></SectionGuard>} />
        <Route path="away" element={<SectionGuard section="away"><AwayChecklist /></SectionGuard>} />
        <Route path="manual" element={<SectionGuard section="manual"><HouseholdManual /></SectionGuard>} />
        <Route path="lease" element={<SectionGuard section="lease"><Lease /></SectionGuard>} />
        <Route path="movelog" element={<SectionGuard section="movelog"><MoveLog /></SectionGuard>} />
        <Route path="maintenance" element={<SectionGuard section="maintenance"><Maintenance /></SectionGuard>} />
        <Route path="housing" element={<SectionGuard section="housing"><HousingCosts /></SectionGuard>} />
        <Route path="utilities" element={<SectionGuard section="utilities"><Utilities /></SectionGuard>} />
        <Route path="pets" element={<SectionGuard section="pets"><Pets /></SectionGuard>} />
        <Route path="home/vehicles" element={<SectionGuard section="vehicles"><Vehicles /></SectionGuard>} />
        <Route path="family/documents" element={<SectionGuard section="vault"><Documents /></SectionGuard>} />
        <Route path="family/vault" element={<Navigate to="../documents" relative="path" replace />} />
        <Route path="home-care" element={<SectionGuard section="home"><HomeCare /></SectionGuard>} />
        <Route path="connect/whos-where" element={<SectionGuard section="checkins"><WhosWhere /></SectionGuard>} />
        <Route path="memories" element={<SectionGuard section="memories"><Memories /></SectionGuard>} />
        <Route path="memories/year/:year" element={<SectionGuard section="memories"><YearReview /></SectionGuard>} />
        <Route path="lists/:listId" element={<SectionGuard section="lists"><ListDetail /></SectionGuard>} />
        <Route path="calendar/family" element={<SectionGuard section="calendar"><CalendarPage /></SectionGuard>} />
        <Route path="calendar/flights" element={<SectionGuard section="calendar"><Flights /></SectionGuard>} />
        <Route path="calendar/links" element={<SectionGuard section="calendar"><CalendarLinks /></SectionGuard>} />
        <Route path="family/billing" element={<Billing />} />
        <Route path="connect/messages" element={<SectionGuard section="connect"><Messages /></SectionGuard>} />
        <Route path="connect/messages/:cid" element={<SectionGuard section="connect"><Messages /></SectionGuard>} />
        <Route path="connect/video" element={<SectionGuard section="connect"><VideoCalls /></SectionGuard>} />
        <Route path="media/shortcuts" element={<SectionGuard section="media"><Shortcuts /></SectionGuard>} />
        <Route path="account/notifications" element={<NotificationSettings />} />
        <Route path="account/settings" element={<AccountSettings />} />
        <Route path="account/gift" element={<GiftPage />} />
        <Route path="account/connected-ai" element={<ConnectedAI />} />
        <Route path="copilot" element={<Copilot />} />
        <Route path="copilot/memory" element={<CopilotMemory />} />
        <Route path="copilot/kids" element={<KidsChats />} />
        <Route path="*" element={<ComingSoon />} />
      </Route>
      <Route path="*" element={<RedirectToHub />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { theme } = useLook();
  return <Toaster theme={theme.scheme} position="top-right" richColors closeButton />;
}

export default function App() {
  return (
    <AuthProvider>
      <LookProvider>
        <BrowserRouter>
          <AppRouter />
          <ThemedToaster />
        </BrowserRouter>
      </LookProvider>
    </AuthProvider>
  );
}
